import { Ng2StateDeclaration, StateService } from '@uirouter/angular';
import { Rejection, Transition } from '@uirouter/core';

import { CimaCore, CimaToken, CimaUrl, SessionStorage, XmStore, XmStoreUtil } from 'core/services';
import { StoreAction } from 'store/actions';
import { Account, User } from 'store/user/models';
import { CmsContactBar, CmsMyAccount, CmsMyAccountModals, CmsUserRoles } from 'store/cms/models';

import { MyAccountBaseComponent } from './base/base';
import { AuthGuard, CustomerGuard, MaintenanceGuard } from '../route-guards';
import { LocationCollection } from 'store/locations/models/location';
import { IXMOptions } from 'core/interfaces';
import { CONFIG_TOKEN, LOCATION_SELECTION_REQUIRED_ERROR, StorageToken } from 'core/constants';

export function GetAccountInfo(cimaToken: CimaToken, xmStore: XmStore, transition: Transition): Promise<Account | ApiErrorType | void> {
    if (!cimaToken.isLoggedIn) {
        return transition.to().name.includes('account.u.') ? Promise.resolve() : Promise.reject();
    }

    const cimaCore: CimaCore = transition.injector().get(CimaCore);

    return XmStoreUtil.defaultCatch(xmStore.findChildPromise<Account, User>(StoreAction.GET_ACCOUNT_INFO, 'account',
        Account, User).then((account: Account) => {
        cimaCore.cbmMandateSelectionMode = false;

        return account;
    }).catch((err: ApiErrorType) => {
        const config: IXMOptions = transition.injector().get(CONFIG_TOKEN);
        const sessionStorage: SessionStorage = transition.injector().get(SessionStorage);
        const isOrionTenant: boolean = sessionStorage.get<string>(StorageToken.X_TENANT_ID) === 'ORION';
        const enableCbmAccountSelector: boolean = config?.ENABLE_CBM_ACCOUNT_SELECTOR && !isOrionTenant;
        const enableOrionAccountSelector: boolean = config?.ENABLE_ORION_ACCOUNT_SELECTOR && isOrionTenant;
        const locationSelectionRequired: boolean = err?.status === 409
            && err?.errors?.some((e: ApiErrorDetails) => e?.error?.errorMessage?.toLowerCase()?.startsWith(LOCATION_SELECTION_REQUIRED_ERROR));
        if (locationSelectionRequired && (enableCbmAccountSelector || enableOrionAccountSelector)) {
            cimaCore.cbmMandateSelectionMode = true;

            if (transition.to().name !== 'account.activity.switch-locations') {
                transition.abort();
                const locationParams: { os?: string; acc?: string } = {};
                if (transition.params().os) {
                    locationParams.os = transition.params().os;
                }
                if (transition.params().acc) {
                    locationParams.acc = transition.params().acc;
                }
                transition.router.stateService.go('account.activity.switch-locations', locationParams);

                return Promise.reject(Rejection.aborted('location selection required'));
            }

            // Already navigating to switch-locations — suppress error to avoid error modal
            return Promise.resolve();
        }

        return Promise.resolve(err);
    }));
}

export function GetCmsMyAccount(xmStore: XmStore): Promise<CmsMyAccount> {
    return XmStoreUtil.defaultCatch(xmStore.findPromise<CmsMyAccount>(StoreAction.GET_MY_ACCOUNT, CmsMyAccount));
}

export function GetCmsContactBar(xmStore: XmStore): Promise<CmsContactBar> {
    return XmStoreUtil.defaultCatch(xmStore.findPromise<CmsContactBar>(StoreAction.GET_CONTACT_BAR, CmsContactBar));
}

export function GetCmsUserRoles(xmStore: XmStore): Promise<CmsUserRoles> {
    return XmStoreUtil.defaultCatch(xmStore.findPromise<CmsUserRoles>(StoreAction.GET_CMS_USER_ROLES, CmsUserRoles));
}

export function GetCmsMyAccountModals(xmStore: XmStore): Promise<CmsMyAccountModals> {
    return XmStoreUtil.defaultCatch(xmStore.findPromise<CmsMyAccountModals>(StoreAction.GET_MY_ACCOUNT_MODALS, CmsMyAccountModals));
}

export function GetCbmLocations(cimaCore: CimaCore, xmStore: XmStore, transition: Transition): Promise<LocationCollection | void> {
    const config: IXMOptions = transition.injector().get(CONFIG_TOKEN);
    const sessionStorage: SessionStorage = transition.injector().get(SessionStorage);
    const isOrionTenant: boolean = sessionStorage?.get<string>(StorageToken.X_TENANT_ID) === 'ORION';
    const enableCbmAccountSelector: boolean = config?.ENABLE_CBM_ACCOUNT_SELECTOR && !isOrionTenant;
    const enableOrionAccountSelector: boolean = config?.ENABLE_ORION_ACCOUNT_SELECTOR && isOrionTenant;

    if (cimaCore.isBusinessUser && (enableCbmAccountSelector || enableOrionAccountSelector)) {
        return XmStoreUtil.defaultCatch(xmStore.findPromise<LocationCollection>(StoreAction.GET_SWITCH_LOCATIONS, LocationCollection));
    } else {
        return Promise.resolve();
    }
}

export const MY_ACCOUNT_ROUTES: Ng2StateDeclaration[] = [
    {
        abstract: true,
        parent: 'xm',
        name: 'account',
        url: '/',
        resolve: [
            {
                token: 'cimaCheck',
                deps: [ CimaUrl, CimaToken, StateService ],
                resolveFn: AuthGuard
            },
            {
                token: 'accountInfo',
                deps: [ CimaToken, XmStore, Transition, 'cimaCheck' ],
                resolveFn: GetAccountInfo
            },
            {
                token: 'cbmLocations',
                deps: [ CimaCore, XmStore, Transition ],
                resolveFn: GetCbmLocations
            },
            {
                token: 'customerCheck',
                deps: [ 'accountInfo', StateService, XmStore, Transition ],
                resolveFn: CustomerGuard
            },
            {
                token: 'maintenanceCheck',
                deps: [ CimaCore, StateService, Transition, 'cimaCheck' ],
                resolveFn: MaintenanceGuard
            },
            {
                token: 'cmsMyAccount',
                deps: [ XmStore ],
                resolveFn: GetCmsMyAccount
            },
            {
                token: 'cmsContactBar',
                deps: [ XmStore ],
                resolveFn: GetCmsContactBar
            },
            {
                token: 'cmsMyAccountModals',
                deps: [ XmStore ],
                resolveFn: GetCmsMyAccountModals
            },
            {
                token: 'cmsUserRoles',
                deps: [ XmStore ],
                resolveFn: GetCmsUserRoles
            }
        ],
        views: {
            'content@xm': {
                component: MyAccountBaseComponent
            }
        }
    },
    {
        name: 'account.landing',
        url: '',
        resolve: [
            {
                token: 'cimaCheck',
                deps: [ CimaUrl, CimaToken, StateService ],
                resolveFn: AuthGuard
            },
            {
                token: 'accountInfo',
                deps: [ CimaToken, XmStore, Transition, 'cimaCheck' ],
                resolveFn: GetAccountInfo
            }],
        redirectTo: 'account.activity.landing'
    },
    {
        name: 'account.activity.**',
        url: 'activity',
        resolve: [
            {
                token: 'cimaCheck',
                deps: [ CimaUrl, CimaToken, StateService ],
                resolveFn: AuthGuard
            },
            {
                token: 'accountInfo',
                deps: [ CimaToken, XmStore, Transition, 'cimaCheck' ],
                resolveFn: GetAccountInfo
            }],
        loadChildren: () => import('./activity/module').then((m: typeof import('./activity/module')) => m.ActivityModule)
    },
    {
        name: 'account.billing.**',
        url: 'billing',
        resolve: [
            {
                token: 'cimaCheck',
                deps: [ CimaUrl, CimaToken, StateService ],
                resolveFn: AuthGuard
            },
            {
                token: 'accountInfo',
                deps: [ CimaToken, XmStore, Transition, 'cimaCheck' ],
                resolveFn: GetAccountInfo
            }],
        loadChildren: () => import('./billing/module').then((m: typeof import('./billing/module')) => m.BillingModule)
    },
    {
        name: 'account.devices.**',
        url: 'devices',
        resolve: [
            {
                token: 'cimaCheck',
                deps: [ CimaUrl, CimaToken, StateService ],
                resolveFn: AuthGuard
            },
            {
                token: 'accountInfo',
                deps: [ CimaToken, XmStore, Transition, 'cimaCheck' ],
                resolveFn: GetAccountInfo
            }],
        loadChildren: () => import('./devices/module').then((m: typeof import('./devices/module')) => m.DevicesModule)
    },
    {
        name: 'account.settings.**',
        url: 'settings',
        resolve: [
            {
                token: 'cimaCheck',
                deps: [ CimaUrl, CimaToken, StateService ],
                resolveFn: AuthGuard
            },
            {
                token: 'accountInfo',
                deps: [ CimaToken, XmStore, Transition, 'cimaCheck' ],
                resolveFn: GetAccountInfo
            }],
        loadChildren: () => import('./settings/module').then((m: typeof import('./settings/module')) => m.SettingsModule)
    },
    {
        name: 'account.u.**',
        url: 'u',
        loadChildren: () => import('./unauthenticated/module').then((m: typeof import('./unauthenticated/module')) => m.UnauthenticatedModule)
    },
    {
        name: 'standalone-cpc',
        abstract: true,
        url: '/standalone-cpc',
        loadChildren: () => import('./../../xm/standalone-cpc/module').then((m: typeof import('./../../xm/standalone-cpc/module')) => m.StandaloneCpcModule)
    }
];
